import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { HERITAGE_ASSETS } from "../dahlias/heritageAssets";

/**
 * Shared portal "hero shell", Heritage stained-glass banner + horizontal nav,
 * inspired by the SmartMeals-style help-desk layout.
 *
 * Used as the top of Admin / Customer / Grower / Management portal home pages.
 *
 * Props:
 *  - kicker      : small caps label, e.g. "✦ HERITAGE · ADMIN ✦"
 *  - title       : main headline ("Dashboard", "Welcome back, Jane")
 *  - subtitle    : optional sub-line of italic copy
 *  - nav         : array of { to, label, active?, testid? }
 *  - rightSlot   : optional JSX (e.g. NEW REQUEST button or notifications)
 *  - userName    : current user's name (rendered top-right)
 *  - onLogout    : optional logout handler
 *  - children    : usually a search bar / CTA inside the banner
 *  - tone        : "plum" (default) or "forest"
 */
export default function PortalHero({
  kicker,
  title,
  subtitle,
  nav = [],
  rightSlot,
  userName,
  onLogout,
  children,
  tone = "plum",
  compact = false,
}) {
  const location = useLocation();
  const accent = tone === "forest" ? "#2F4A2A" : "#6E1E3A";

  return (
    <header data-testid="portal-hero" className="relative text-[#FBE6D8] overflow-hidden">
      {/* Stained-glass banner */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url(${HERITAGE_ASSETS.heroScene})`,
          backgroundPosition: "center 18%",
          backgroundSize: "115% auto",
        }}
      />
      {/* Dark wash so text reads */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(31,45,27,0.55) 0%, rgba(31,45,27,0.45) 45%, rgba(31,27,28,0.78) 100%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        {/* Top row, logo + nav + user */}
        <div className="flex items-center justify-between gap-4 py-4 md:py-5">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="portal-home">
            <img
              src="/photos/brand/heritage-seal.png"
              alt="Heritage Dahlias"
              className="w-11 h-11 md:w-12 md:h-12 drop-shadow-md"
              draggable="false"
            />
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-display tracking-[0.16em] uppercase text-base md:text-lg text-[#FBE6D8] drop-shadow">
                Heritage Dahlias
              </span>
              <span className="font-label tracking-[0.4em] text-[9px] md:text-[10px] uppercase text-[#FBE6D8]/75">
                Est. 1932
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {nav.map((n) => {
              const isActive =
                n.active ?? (n.to ? location.pathname === n.to : false);
              return (
                <Link
                  key={n.label}
                  to={n.to || "#"}
                  onClick={n.onClick}
                  data-testid={n.testid || `portal-nav-${slug(n.label)}`}
                  className={`inline-flex items-center gap-1.5 px-3 lg:px-4 py-2 wobble-pill text-[12px] lg:text-sm font-label tracking-[0.25em] uppercase transition ${
                    isActive
                      ? "bg-[#FBE6D8] text-[#6E1E3A] shadow-md"
                      : "text-[#FBE6D8]/90 hover:bg-[#FBE6D8]/15"
                  }`}
                >
                  {n.Icon && <n.Icon className="w-4 h-4" />}
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {rightSlot}
            {userName && (
              <span className="hidden lg:inline text-sm text-[#FBE6D8]/85 px-2">
                {userName}
              </span>
            )}
            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                data-testid="portal-logout"
                className="inline-flex items-center gap-1.5 h-9 px-3 wobble-pill border border-[#FBE6D8]/35 text-[#FBE6D8]/95 hover:bg-[#FBE6D8]/15 text-xs uppercase font-label tracking-[0.25em]"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            )}
          </div>
        </div>

        {/* Banner content */}
        <div className={compact ? "py-5 md:py-7" : "py-10 md:py-16 lg:py-20"}>
          {kicker && (
            <span
              className="font-label tracking-[0.5em] text-[11px] md:text-xs uppercase drop-shadow-[0_2px_6px_rgba(20,10,5,0.5)]"
              style={{ color: "#FBE6D8" }}
            >
              {kicker}
            </span>
          )}
          {title && (
            <h1
              data-testid="portal-hero-title"
              className={`mt-3 font-display text-[#FBE6D8] leading-[1.05] drop-shadow-[0_3px_12px_rgba(20,10,5,0.55)] ${
                compact ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl lg:text-6xl"
              }`}
            >
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="mt-3 font-display-italic text-lg md:text-xl text-[#FBE6D8]/90 max-w-2xl drop-shadow-[0_2px_8px_rgba(20,10,5,0.55)]">
              {subtitle}
            </p>
          )}
          {children && <div className="mt-7 max-w-3xl">{children}</div>}
        </div>

        {/* Mobile horizontal nav */}
        {nav.length > 0 && (
          <nav className="md:hidden flex overflow-x-auto -mx-5 px-5 pb-3 gap-2">
            {nav.map((n) => {
              const isActive =
                n.active ?? (n.to ? location.pathname === n.to : false);
              return (
                <Link
                  key={n.label}
                  to={n.to || "#"}
                  onClick={n.onClick}
                  data-testid={n.testid || `portal-nav-m-${slug(n.label)}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 wobble-pill text-[11px] font-label tracking-[0.25em] uppercase ${
                    isActive
                      ? "bg-[#FBE6D8] text-[#6E1E3A]"
                      : "text-[#FBE6D8]/90 bg-[#FBE6D8]/10 border border-[#FBE6D8]/25"
                  }`}
                >
                  {n.Icon && <n.Icon className="w-3.5 h-3.5" />}
                  {n.label}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
      {/* coloured base line that bridges into the body */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 right-0 h-1"
        style={{ background: accent }}
      />
    </header>
  );
}

/**
 * Feature card grid, the SmartMeals "Device Support / Email / Payroll / Training" tiles.
 * Drop below <PortalHero/> for the dashboard home of any portal.
 *
 * Items: [{ to, Icon, title, description, testid? }]
 */
export function FeatureCardGrid({ items = [], cols = 4, accent = "#6E1E3A" }) {
  return (
    <section
      data-testid="portal-feature-grid"
      className="max-w-7xl mx-auto px-5 md:px-8 -mt-12 md:-mt-16 relative z-10"
    >
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${cols} gap-4 md:gap-5`}
      >
        {items.map((it, i) => (
          <Link
            key={it.title}
            to={it.to || "#"}
            onClick={it.onClick}
            data-testid={it.testid || `feature-card-${slug(it.title)}`}
            className="group bg-white border-2 border-[#6E1E3A]/20 hover:border-[#6E1E3A]/60 p-6 md:p-7 wobble-a shadow-md hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {it.Icon && (
              <span
                className="w-12 h-12 md:w-14 md:h-14 grid place-items-center wobble-blob shrink-0"
                style={{
                  background: `${accent}14`,
                  color: accent,
                }}
              >
                <it.Icon className="w-6 h-6 md:w-7 md:h-7" />
              </span>
            )}
            <h3 className="mt-4 font-display text-xl md:text-2xl text-[var(--dahlias-forest)] leading-tight group-hover:text-[#6E1E3A] transition-colors">
              {it.title}
            </h3>
            {it.description && (
              <p className="mt-2 text-sm md:text-base text-[var(--dahlias-ink)]/70 leading-relaxed">
                {it.description}
              </p>
            )}
            {it.badge && (
              <span
                className="mt-3 inline-flex w-fit items-center gap-1 px-2 py-0.5 wobble-pill text-[10px] tracking-[0.3em] uppercase font-label"
                style={{ background: `${accent}14`, color: accent }}
              >
                {it.badge}
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}

function slug(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}
